import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { prepareLogsDateRange } from 'infrastructure/functions/logs';
import { ThresholdLog } from 'infrastructure/classes/logs/threshold-log';

import useApi from '../use-api';

import type { DateRange } from 'infrastructure/types';
import type {
  IPaginationMeta,
  IPartialPaginationMeta,
  IPaginatedThresholdLogs,
} from 'infrastructure/interfaces';
import type { Dispatch, SetStateAction } from 'react';

const thresholdLogApi = (patientGuid: string) =>
  `core/patients/${patientGuid}/threshold-logs`;

interface IUseThresholdLogMeta {
  dateRange?: DateRange;
}

type mergedMeta = IUseThresholdLogMeta & IPaginationMeta;

type UseLog = {
  loading: boolean;
  items: Array<ThresholdLog>;
  meta: mergedMeta;
  setMeta: Dispatch<SetStateAction<mergedMeta>>;
};

interface IUseLogProps {
  patientGuid: string;
  meta?: IUseThresholdLogMeta & IPartialPaginationMeta;
}

export const useThresholdLog = (props: IUseLogProps): UseLog => {
  const options = props;
  const { patientGuid, meta: metaProps = {} } = options;

  const [meta, setMeta] = useState<mergedMeta>({
    page: 1,
    items: 15,
    totalCount: 0,
    ...metaProps,
  });

  const { loadData } = useApi();

  const queryKey = [
    thresholdLogApi(patientGuid),
    prepareLogsDateRange(meta.dateRange),
    meta.page,
  ];

  const { data = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () =>
      loadData<IPaginatedThresholdLogs>(thresholdLogApi(patientGuid), {
        page: meta.page,
        items: meta.items,
        ...prepareLogsDateRange(meta.dateRange),
      }).then((res) => {
        if (res) {
          const { count, data: rows } = res;

          setMeta({
            ...meta,
            totalCount: count,
          });

          return rows.map((el) => new ThresholdLog(el));
        }
      }),
    enabled: Boolean(patientGuid),
  });

  return {
    loading: isLoading,
    items: data,
    meta,
    setMeta,
  };
};
